import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApps } from '@/contexts/AppsContext';
import { useAppId } from '@/hooks/useAppId';
import { apiKeysApi } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Copy, Check, AlertTriangle } from 'lucide-react';

export default function CreateKeyPage() {
  const navigate = useNavigate();
  const appId = useAppId();
  const { currentApp, checkAppReadiness } = useApps();

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!currentApp) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/apps/${appId}/keys`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to API Keys
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Select an app from the sidebar first.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Key name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await apiKeysApi.create(appId, {
        name,
      });
      setCreatedKey(response.data.data);
      checkAppReadiness(appId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (createdKey?.plainKey) {
      await navigator.clipboard.writeText(createdKey.plainKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setCreatedKey(null);
    navigate(`/apps/${appId}/keys`);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(`/apps/${appId}/keys`)}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to API Keys
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Create API Key</CardTitle>
          <CardDescription>
            Create a new API key for <strong>{currentApp.name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Key Name *</Label>
              <Input
                id="name"
                placeholder="Production API Key"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                A descriptive name to help you identify this key
              </p>
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/apps/${appId}/keys`)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Creating...
                  </>
                ) : (
                  'Create Key'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium mb-1">Important Security Note</h4>
              <p className="text-sm text-muted-foreground">
                API keys provide full access to your app's resources. Keep them
                secure and never expose them in client-side code or public
                repositories.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Created Key Dialog */}
      <Dialog open={!!createdKey} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-500" />
              <span>API Key Created</span>
            </DialogTitle>
            <DialogDescription>
              Your API key has been created successfully. Make sure to copy it now
              — you won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                  {createdKey?.plainKey}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyToClipboard}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="rounded-md bg-yellow-500/10 p-3 text-sm text-yellow-600 dark:text-yellow-400">
              <strong>Warning:</strong> This is the only time you'll see this key.
              Store it securely.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={copyToClipboard} variant="outline">
              {copied ? 'Copied!' : 'Copy Key'}
            </Button>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
